import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { environment } from '@src/environments/environment';
import { Observable } from 'rxjs';
import { ServicioSesion } from '@app/services/sesion/sesion.service';
import {
  CrearEscuelaPayload, EscuelaResponse, EscuelasResponse,
  EscuelaConfig, EscuelaConfigResponse,
  EscuelaDocumentosResponse, EscuelaDocumentoResponse,
  CrearEscuelaDocumento, SubirArchivoEscuelaDocumento, ActualizarStatusEscuelaDocumento,
  WaSaldo, WaCreditTransaccionesResponse, CrearWaCreditTransaccionPayload, WaCreditTransaccionResponse,
} from '@app/models/escuelas/escuela.model';

@Injectable({
  providedIn: 'root',
})
export class EscuelasService {
  private _http = inject(HttpClient);
  private _url = environment.url;
  private _servicioSesion = inject(ServicioSesion);

  private _getAuthHeaders(): HttpHeaders {
    const token = this._servicioSesion.obtenerToken();
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`
    });
  }

  public getEscuelas(): Observable<EscuelasResponse> {
    return this._http.get<EscuelasResponse>(`${this._url}/escuelas`, {
      headers: this._getAuthHeaders()
    });
  }

  public getEscuelaPorId(id: number): Observable<EscuelaResponse> {
    return this._http.get<EscuelaResponse>(`${this._url}/escuelas/${id}`, {
      headers: this._getAuthHeaders()
    });
  }

  public crearEscuela(payload: CrearEscuelaPayload): Observable<EscuelaResponse> {
    return this._http.post<EscuelaResponse>(`${this._url}/escuelas`, payload, {
      headers: this._getAuthHeaders()
    });
  }

  public actualizarEscuela(id: number, payload: Partial<CrearEscuelaPayload>): Observable<EscuelaResponse> {
    return this._http.put<EscuelaResponse>(`${this._url}/escuelas/${id}`, payload, {
      headers: this._getAuthHeaders()
    });
  }

  /** GET /escuelas/{id}/config — el backend crea el registro si la escuela todavía no tiene uno. */
  public getEscuelaConfig(id: number): Observable<EscuelaConfigResponse> {
    return this._http.get<EscuelaConfigResponse>(`${this._url}/escuelas/${id}/config`, {
      headers: this._getAuthHeaders()
    });
  }

  public actualizarEscuelaConfig(
    id: number,
    data: Partial<Pick<EscuelaConfig, 'color_primary' | 'color_primary_light' | 'color_secondary' | 'color_titulo' | 'color_bg' | 'fondoMenu' | 'textoMenu' | 'darkModeDefault' | 'wa_monthly_limit' | 'accesosActivos' | 'urlActualizarAlumno' | 'noticiasActivas' | 'nombreFeedNoticias'>>
  ): Observable<EscuelaConfigResponse> {
    return this._http.put<EscuelaConfigResponse>(`${this._url}/escuelas/${id}/config`, data, {
      headers: this._getAuthHeaders()
    });
  }

  public getDocumentos(escuelaId: number): Observable<EscuelaDocumentosResponse> {
    return this._http.get<EscuelaDocumentosResponse>(`${this._url}/escuelas/${escuelaId}/documentos`, {
      headers: this._getAuthHeaders()
    });
  }

  public crearDocumento(escuelaId: number, datos: CrearEscuelaDocumento): Observable<EscuelaDocumentoResponse> {
    return this._http.post<EscuelaDocumentoResponse>(`${this._url}/escuelas/${escuelaId}/documentos`, datos, {
      headers: this._getAuthHeaders()
    });
  }

  public actualizarStatusDocumento(
    escuelaId: number, documentoId: number, datos: ActualizarStatusEscuelaDocumento
  ): Observable<EscuelaDocumentoResponse> {
    return this._http.patch<EscuelaDocumentoResponse>(
      `${this._url}/escuelas/${escuelaId}/documentos/${documentoId}/status`, datos, { headers: this._getAuthHeaders() }
    );
  }

  public subirArchivoDocumento(
    escuelaId: number, documentoId: number, datos: SubirArchivoEscuelaDocumento
  ): Observable<EscuelaDocumentoResponse> {
    return this._http.patch<EscuelaDocumentoResponse>(
      `${this._url}/escuelas/${escuelaId}/documentos/${documentoId}/subir`, datos, { headers: this._getAuthHeaders() }
    );
  }

  public eliminarDocumento(escuelaId: number, documentoId: number): Observable<{ message: string }> {
    return this._http.delete<{ message: string }>(
      `${this._url}/escuelas/${escuelaId}/documentos/${documentoId}`, { headers: this._getAuthHeaders() }
    );
  }

  public getWaSaldo(escuelaId: number): Observable<WaSaldo> {
    return this._http.get<WaSaldo>(`${this._url}/whatsapp/saldo`, {
      headers: this._getAuthHeaders(),
      params: { escuela_id: escuelaId }
    });
  }

  public getWaTransacciones(escuelaId: number, page: number = 1, perPage: number = 10): Observable<WaCreditTransaccionesResponse> {
    return this._http.get<WaCreditTransaccionesResponse>(`${this._url}/whatsapp/transacciones`, {
      headers: this._getAuthHeaders(),
      params: { escuela_id: escuelaId, per_page: perPage, page }
    });
  }

  public crearWaTransaccion(data: CrearWaCreditTransaccionPayload): Observable<WaCreditTransaccionResponse> {
    return this._http.post<WaCreditTransaccionResponse>(`${this._url}/whatsapp/transacciones`, data, {
      headers: this._getAuthHeaders()
    });
  }
}
